/**
 * PRP-WEB-2 W2.3: what the right-click menu offers, per thing clicked. Pure: each item carries
 * a `run` that calls the editor context (`MenuCtx`), so the lists are tested without a DOM.
 * Every plan change is one `ctx.act` (one undo step); grouping is one `ctx.setGroups`.
 */
import {
  POLICY, addCloset, addOpening, deleteOpening, deleteRoom, dist, doSplit, edgeOf, findRoom, removeWall, restoreWall,
  sharedMap, toggleHinge, toggleSwing, updateOpening, wallMissing, renameRoom, setCeiling, setCeilingType,
  addRoomOfType, roomTypes,
  type CeilingV2, type Plan, type Pt, type Room,
} from '@sketch-core';
import { addToGroup, groupOf, removeFromGroup, reorderGroup, type Groups, type GroupResult } from '../groups/groups';

export type MenuTarget =
  | { kind: 'wall'; rid: string; vid: number; off: number; wp: Pt }
  | { kind: 'open'; rid: string; oid: number; wp: Pt }
  | { kind: 'room'; rid: string; wp: Pt }
  | { kind: 'empty'; wp: Pt };

export interface MenuItem {
  id: string;
  label: string;
  /** Why it cannot be used here (shown greyed, with this as its title). */
  disabled?: string;
  danger?: boolean;
  /** A submenu instead of an action. */
  items?: MenuItem[];
  run?: () => void;
}

type Act = (p: Plan) => { ok: boolean; message?: string };

/** A value the user types or picks (the menu's "…" items). */
export type PromptSpec =
  | { kind: 'length'; title: string; valueFt: number; apply: (ft: number) => string | void }
  | { kind: 'text'; title: string; value: string; apply: (text: string) => string | void }
  | { kind: 'choice'; title: string; value: string; choices: { value: string; label: string }[]; apply: (v: string) => string | void };

export interface MenuCtx {
  plan: Plan;
  groups: Groups;
  /** Null when the room may be changed here, else why not (DD7). */
  editable: (rid: string) => string | null;
  /** Run a core action as one undo step; returns the refusal message, if any. */
  act: (a: Act, after?: { select?: { type: 'room'; rid: string } }) => string | null;
  setGroups: (fn: (g: Groups) => GroupResult) => string | null;
  prompt: (p: PromptSpec) => void;
  copy: (rid: string) => void;
  canPaste: boolean;
  paste: (at: Pt) => void;
  zoomFit: () => void;
  /** A new room's id (a uuid: the server creates its marker under it). */
  newId: () => string;
  /** Set a wall's length (the panel's Wall length rule). */
  wallLength: (rid: string, vid: number, ft: number) => string | null;
  /** Clip a corner 45° (web-2-core `chamferCorner`); null until that lane lands. */
  chamfer: ((rid: string, vid: number, end: 'start' | 'end') => string | null) | null;
}

const item = (id: string, label: string, run: () => void, extra: Partial<MenuItem> = {}): MenuItem => ({ id, label, run, ...extra });

function wallMenu(t: Extract<MenuTarget, { kind: 'wall' }>, ctx: MenuCtx): MenuItem[] {
  const { plan } = ctx;
  const r = findRoom(plan, t.rid);
  const E = r && edgeOf(r, t.vid);
  if (!r || !E) return [];
  const ro = ctx.editable(t.rid) ?? undefined;
  const L = dist(E.a, E.b);
  const straight = Math.abs(E.a.x - E.b.x) < 1e-6 || Math.abs(E.a.y - E.b.y) < 1e-6;
  const missing = wallMissing(r, t.vid);
  const shared = (sharedMap(plan).get(t.rid)?.get(t.vid) ?? []).length > 0;
  const near = t.off < L / 2 ? 'start' : 'end';
  return [
    item('wall-length', 'Set length…', () => ctx.prompt({
      kind: 'length', title: `Wall length (${r.name})`, valueFt: L,
      apply: (ft) => ctx.wallLength(t.rid, t.vid, ft) ?? undefined,
    }), { disabled: ro ?? (straight ? undefined : 'Angled walls are set by the walls next to them.') }),
    missing
      ? item('wall-real', 'Make real wall', () => ctx.act((p) => restoreWall(p, t.rid, missing.id)), { disabled: ro })
      : item('wall-missing', 'Make missing wall', () => ctx.act((p) => removeWall(p, t.rid, t.vid)),
        { disabled: ro ?? (shared ? undefined : 'Only a wall between two rooms can be a missing wall.') }),
    item('wall-door', 'Add door', () => ctx.act((p) => addOpening(p, t.rid, t.vid, t.off, 'door')), { disabled: ro }),
    item('wall-window', 'Add window', () => ctx.act((p) => addOpening(p, t.rid, t.vid, t.off, 'window')), { disabled: ro }),
    item('wall-closet', 'Add closet here', () => {
      const id = ctx.newId();
      ctx.act((p) => addCloset(p, t.rid, t.vid, t.off, { id, type: 'reach_in_closet' }), { select: { type: 'room', rid: id } });
    }, { disabled: ro ?? (straight ? undefined : 'Pick a straight wall.') }),
    item('wall-chamfer', 'Clip corner 45°', () => { ctx.chamfer?.(t.rid, t.vid, near); },
      { disabled: ro ?? (ctx.chamfer ? undefined : 'Coming with the next update.') }),
    item('wall-split', 'Split room here', () => {
      const id = ctx.newId();
      ctx.act((p) => doSplit(p, t.rid, t.vid, t.off, { id }));
    }, { disabled: ro ?? (straight ? undefined : 'Pick a straight wall.') }),
  ];
}

function doorMenu(t: Extract<MenuTarget, { kind: 'open' }>, ctx: MenuCtx): MenuItem[] {
  const r = findRoom(ctx.plan, t.rid);
  const o = r?.openings.find((q) => q.id === t.oid);
  if (!r || !o) return [];
  const ro = ctx.editable(t.rid) ?? undefined;
  const name = o.kind === 'door' ? 'door' : o.kind === 'window' ? 'window' : 'opening';
  const isDoor = o.kind === 'door';
  return [
    ...(isDoor ? [
      item('door-swing', 'Flip swing (in / out)', () => ctx.act((p) => toggleSwing(p, t.rid, t.oid)), { disabled: ro }),
      item('door-hinge', 'Flip hinge side', () => ctx.act((p) => toggleHinge(p, t.rid, t.oid)), { disabled: ro }),
      item('door-type', 'Door type…', () => ctx.prompt({
        kind: 'choice', title: 'Door type', value: o.door?.type ?? POLICY.door_types[0],
        choices: POLICY.door_types.map((d: string) => ({ value: d, label: d })),
        apply: (v) => ctx.act((p) => updateOpening(p, t.rid, t.oid, { door: { type: v } })) ?? undefined,
      }), { disabled: ro }),
    ] : []),
    item('open-width', 'Width…', () => ctx.prompt({
      kind: 'length', title: `${name[0].toUpperCase()}${name.slice(1)} width`, valueFt: o.widthFt,
      apply: (ft) => ctx.act((p) => updateOpening(p, t.rid, t.oid, { widthFt: ft })) ?? undefined,
    }), { disabled: ro }),
    item('open-delete', `Delete ${name}`, () => ctx.act((p) => deleteOpening(p, t.rid, t.oid)), { disabled: ro, danger: true }),
  ];
}

const CEILING_LABEL: Record<string, string> = { flat: 'Flat', sloped: 'Sloped', peaked: 'Peaked', vaulted: 'Vaulted', tray: 'Tray', box: 'Box', attic: 'Attic' };

function roomMenu(t: Extract<MenuTarget, { kind: 'room' }>, ctx: MenuCtx): MenuItem[] {
  const { plan, groups } = ctx;
  const r = findRoom(plan, t.rid);
  if (!r) return [];
  const ro = ctx.editable(t.rid) ?? undefined;
  const g = groupOf(groups, t.rid);
  // Review W7: only rooms on this room's level can take it as a subroom (addToGroup refuses the rest).
  const hosts = plan.rooms.filter((q) => q.id !== t.rid && groupOf(groups, q.id)?.role !== 'sub' && q.level === r.level);
  const sub = g && g.role === 'sub' ? { main: g.group.main_room_id, n: g.order!, of: g.group.subroom_ids.length } : null;
  const isMain = g?.role === 'main';
  return [
    item('room-rename', 'Rename…', () => ctx.prompt({
      kind: 'text', title: 'Room name', value: r.name, apply: (v) => ctx.act((p) => renameRoom(p, t.rid, v)) ?? undefined,
    }), { disabled: ro }),
    item('room-ceiling', 'Ceiling height…', () => ctx.prompt({
      kind: 'length', title: `Ceiling height (${r.name})`, valueFt: r.ceilingFt,
      apply: (ft) => ctx.act((p) => setCeiling(p, t.rid, ft)) ?? undefined,
    }), { disabled: ro }),
    item('room-ceiling-type', 'Ceiling type…', () => ctx.prompt({
      kind: 'choice', title: `Ceiling type (${r.name})`, value: r.ceiling?.type ?? 'flat',
      choices: Object.keys(POLICY.ceiling_types).map((k) => ({ value: k, label: CEILING_LABEL[k] ?? k })),
      apply: (v) => ctx.act((p) => setCeilingType(p, t.rid, ceilingDraft(v, r))) ?? undefined,
    }), { disabled: ro }),
    item('room-copy', 'Copy', () => ctx.copy(t.rid)),
    sub
      ? item('room-ungroup', 'Remove from group', () => ctx.setGroups((G) => ({ ok: true, groups: removeFromGroup(G, t.rid) })))
      : {
        id: 'room-subroom', label: 'Make subroom of…',
        disabled: isMain ? 'This room has subrooms of its own.' : hosts.length ? undefined : 'There is no other room to join.',
        items: hosts.map((h) => item(`room-subroom-${h.id}`, h.name, () => ctx.setGroups((G) => addToGroup(G, h.id, t.rid, undefined, ctx.plan)))),
      },
    ...(sub ? [
      item('room-up', 'Move up in order', () => ctx.setGroups((G) => reorderGroup(G, sub.main, t.rid, sub.n - 2)),
        { disabled: sub.n <= 1 ? 'It is already first.' : undefined }),
      item('room-down', 'Move down in order', () => ctx.setGroups((G) => reorderGroup(G, sub.main, t.rid, sub.n)),
        { disabled: sub.n >= sub.of ? 'It is already last.' : undefined }),
    ] : []),
    item('room-delete', 'Delete room', () => ctx.act((p) => deleteRoom(p, t.rid)), { disabled: ro, danger: true }),
  ];
}

/**
 * A ceiling of type `type` drafted from the room's flat height (the phone's starting values,
 * screen policy `ceiling_draft`), running from the room's first wall; the user refines it on
 * the phone or in Xactimate.
 */
export function ceilingDraft(type: string, r: Room): CeilingV2 {
  const h = r.ceilingFt;
  const xs = r.corners.map((p) => p.x), ys = r.corners.map((p) => p.y);
  const side = Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
  const orientation = { edgeStart: r.corners[0].id };
  switch (type) {
    case 'sloped': return { type, shortFt: h, tallFt: h + 2, orientation } as unknown as CeilingV2;
    case 'peaked': return { type, shortFt: h, tallFt: h, peakFt: h + 2, peakOffsetFt: side / 2, orientation } as unknown as CeilingV2;
    case 'vaulted': return { type, wallFt: h, peakFt: h + 2, orientation } as unknown as CeilingV2;
    case 'tray': case 'box': return { type, edgeFt: h, offsetFt: Math.min(1, side / 4), upFt: 1 } as unknown as CeilingV2;
    case 'attic': return { type, wallFt: h, flatFt: h + 2, flatWidthFt: Math.min(4, side / 2), orientation } as unknown as CeilingV2;
    default: return { type: 'flat', heightFt: h } as CeilingV2;
  }
}

function emptyMenu(t: Extract<MenuTarget, { kind: 'empty' }>, ctx: MenuCtx): MenuItem[] {
  return [
    {
      id: 'add-room', label: 'Add room',
      items: roomTypes().map((rt) => item(`add-${rt.type}`, rt.label, () => {
        const id = ctx.newId();
        ctx.act((p) => addRoomOfType(p, rt.type, { center: t.wp }, { id }), { select: { type: 'room', rid: id } });
      })),
    },
    item('paste', 'Paste room', () => ctx.paste(t.wp), { disabled: ctx.canPaste ? undefined : 'Copy a room first.' }),
    item('zoom-fit', 'Zoom to fit', ctx.zoomFit),
  ];
}

export function menuFor(t: MenuTarget, ctx: MenuCtx): MenuItem[] {
  if (t.kind === 'wall') return wallMenu(t, ctx);
  if (t.kind === 'open') return doorMenu(t, ctx);
  if (t.kind === 'room') return roomMenu(t, ctx);
  return emptyMenu(t, ctx);
}

/** The menu's heading. */
export function menuTitle(t: MenuTarget, plan: Plan): string {
  if (t.kind === 'empty') return 'Floor plan';
  const r = findRoom(plan, t.rid);
  if (t.kind === 'room') return r?.name ?? 'Room';
  if (t.kind === 'wall') return `Wall of ${r?.name ?? 'room'}`;
  const o = r?.openings.find((q) => q.id === t.oid);
  return `${o?.kind === 'window' ? 'Window' : o?.kind === 'missing' ? 'Opening' : 'Door'} in ${r?.name ?? 'room'}`;
}
